import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface PaletteColor {
    extraLight?: string;
  }
  interface SimplePaletteColorOptions {
    extraLight?: string;
  }
  interface Palette {
    brandMuted: string;
    brandNavActiveBg: string;
  }
  interface PaletteOptions {
    brandMuted?: string;
    brandNavActiveBg?: string;
  }
  interface TypeText {
    dark: string;
  }
}

// ─── Brand Colors ────────────────────────────────────────────────────────────
// Default brand color. All primary/hero/accent values are derived from this.
// Swap only this value (or let the user pick via themeStore) to re-theme the entire app.
export const DEFAULT_BRAND_COLOR = '#2F2775';

// Kept for any legacy references — derived from DEFAULT_BRAND_COLOR at build time.
export const BRAND_COLORS = {
  primary: '#4B3F9E',
  primaryLight: '#E4DAFF',
  primaryDark: '#2D2580',
} as const;
// ─────────────────────────────────────────────────────────────────────────────

// ─── Color Utilities ─────────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '').padEnd(6, '0');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    case b:
      h = ((r - g) / d + 4) / 6;
      break;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return (
    '#' +
    [f(0), f(8), f(4)]
      .map((x) =>
        Math.round(x * 255)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

/**
 * Primary light — brand hue at full saturation, 92% lightness.
 * Default brand #4B3F9E → #E4DAFF. Used for active chip backgrounds,
 * selected state fills directly tied to the brand color.
 */
export function lightenAccent(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s] = rgbToHsl(r, g, b);
    if (s < 10) return hslToHex(0, 0, 88);
    return hslToHex(h, 100, 88);
  } catch {
    return '#E4DAFF';
  }
}

/**
 * Secondary light — brand hue at full saturation, 97% lightness.
 * Default brand #4B3F9E → #F4F1FF. Used for hover states and
 * secondary chip/tag backgrounds.
 */
export function extraLightenAccent(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s] = rgbToHsl(r, g, b);
    if (s < 10) return hslToHex(0, 0, 97);
    return hslToHex(h, 100, 97);
  } catch {
    return '#F4F1FF';
  }
}

/**
 * Returns the active/hover background for sidebar nav items.
 * Uses the brand color's own hue + saturation at 65% lightness, 12% alpha.
 * For near-grey/black colors (s < 10%), falls back to a neutral grey tint
 * so the active state still looks intentional rather than snapping to a random hue.
 */
export function brandNavActiveBg(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s] = rgbToHsl(r, g, b);
    if (s < 10) {
      return 'rgba(120, 120, 120, 0.15)';
    }
    const [vr, vg, vb] = hexToRgb(hslToHex(h, Math.min(s + 20, 100), 65));
    return `rgba(${vr}, ${vg}, ${vb}, 0.12)`;
  } catch {
    return 'rgba(122, 77, 255, 0.12)';
  }
}

/**
 * Returns a muted brand-tinted text color — same hue as the brand color,
 * heavily desaturated (~17%) at ~45% lightness. Matches the design reference:
 * default brand #4B3F9E → muted text rgba(95, 98, 133, 1).
 */
export function brandMutedColor(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s] = rgbToHsl(r, g, b);
    if (s < 10) return hslToHex(0, 0, 50);
    return hslToHex(h, 17, 45);
  } catch {
    return '#6E7196';
  }
}

/** primary.dark — same hue/sat, L-20%. Default #4B3F9E → #2D2580. */
export function darkenAccent(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s, l] = rgbToHsl(r, g, b);
    if (s < 10) return hslToHex(0, 0, Math.max(l - 20, 5));
    return hslToHex(h, s, Math.max(l - 20, 5));
  } catch {
    return '#2D2580';
  }
}

/** secondary.main — hue, 80% sat, 60% lightness. Default #4B3F9E → #7B6FD4. */
export function secondaryMain(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s] = rgbToHsl(r, g, b);
    if (s < 10) return hslToHex(0, 0, 60);
    return hslToHex(h, 80, 60);
  } catch {
    return '#7B6FD4';
  }
}

/** secondary.dark — hue, 70% sat, 50% lightness. Default #4B3F9E → #6456B8. */
export function secondaryDark(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s] = rgbToHsl(r, g, b);
    if (s < 10) return hslToHex(0, 0, 45);
    return hslToHex(h, 70, 50);
  } catch {
    return '#6456B8';
  }
}

// ─────────────────────────────────────────────────────────────────────────────

// ─── Hero Gradient Utilities ──────────────────────────────────────────────────

/**
 * Mid stop (40%) — same hue, S+6%, L+11%. Lightens and slightly saturates the brand color.
 * Target: brand #2F2775 (248°,48%,31%) → #4133A5 (248°,54%,42%)
 * For near-achromatic colors (S<15%) hue is meaningless — lighten only, no saturation boost.
 */
export function deriveHeroFrom(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s, l] = rgbToHsl(r, g, b);
    if (s < 15) return hslToHex(h, s, Math.min(l + 11, 85));
    return hslToHex(h, Math.min(s + 6, 100), Math.min(l + 11, 85));
  } catch {
    return '#4133A5';
  }
}

/**
 * End stop (100%) — hue +7°, S+52% capped at 100%, L+34% capped at 85%.
 * Produces a vivid bright pop. Target: brand #2F2775 (248°,48%,31%) → #7A4DFF (255°,100%,65%)
 * For near-achromatic colors (S<15%) skip hue shift and saturation boost — lighten only.
 */
export function deriveHeroTo(hex: string): string {
  try {
    const [r, g, b] = hexToRgb(hex);
    const [h, s, l] = rgbToHsl(r, g, b);
    if (s < 15) return hslToHex(h, s, Math.min(l + 34, 85));
    return hslToHex((h + 7) % 360, Math.min(s + 52, 100), Math.min(l + 34, 85));
  } catch {
    return '#7A4DFF';
  }
}

/**
 * Injects --hero-from-rgb, --hero-via-rgb, --hero-to-rgb onto :root.
 * 0% = brand color itself, 40% = lightened/saturated mid, 100% = vivid bright end.
 *   linear-gradient(135deg, rgb(var(--hero-from-rgb)) 0%, rgb(var(--hero-via-rgb)) 40%, rgb(var(--hero-to-rgb)) 100%)
 */
export function injectHeroCssVars(brandColor: string): void {
  const from = brandColor;
  const via = deriveHeroFrom(brandColor);
  const to = deriveHeroTo(brandColor);

  const toRgbString = (hex: string) => hexToRgb(hex).join(', ');

  const focusBorder = lightenAccent(brandColor);

  const root = document.documentElement;
  root.style.setProperty('--hero-from-rgb', toRgbString(from));
  root.style.setProperty('--hero-via-rgb', toRgbString(via));
  root.style.setProperty('--hero-to-rgb', toRgbString(to));
  root.style.setProperty('--hero-focus-border', focusBorder);
}

// ─────────────────────────────────────────────────────────────────────────────

const THEME_SHAPE = {
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: 500 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        },
      },
    },
  },
};

/**
 * Creates a full MUI theme from a single brand color.
 * Derives primary palette, secondary/accent palette, and injects hero CSS vars —
 * all from the one picked color so everything stays cohesive.
 */
export function createAppTheme(brandColor: string) {
  const primaryLight = lightenAccent(brandColor); // hue, 100% sat, 93% L → #E8E0FF
  const primaryDark = darkenAccent(brandColor); // hue, sat,      L-20% → #2D2580
  const secMain = secondaryMain(brandColor); // hue, 80% sat,  60% L → #7B6FD4
  const secDark = secondaryDark(brandColor); // hue, 70% sat,  50% L → #6456B8
  const secLight = extraLightenAccent(brandColor); // hue, 100% sat, 97% L → #F4F1FF
  const muted = brandMutedColor(brandColor);
  const navActiveBg = brandNavActiveBg(brandColor);

  injectHeroCssVars(brandColor);

  return createTheme({
    palette: {
      primary: {
        main: brandColor, // #4B3F9E
        light: primaryLight, // #E8E0FF
        dark: primaryDark, // #2D2580
        contrastText: '#fff',
      },
      secondary: {
        main: secMain, // #7B6FD4
        light: secLight, // #F4F1FF
        dark: secDark, // #6456B8
        extraLight: secLight,
        contrastText: '#fff',
      },
      background: {
        default: '#F9F6FC',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#212121',
        secondary: '#757575',
        dark: '#000000',
      },
      error: {
        main: '#E11D48',
      },
      brandMuted: muted,
      brandNavActiveBg: navActiveBg,
    },
    ...THEME_SHAPE,
  });
}

// ─── Default static theme (used as fallback / initial render) ─────────────────
export const theme = createAppTheme(DEFAULT_BRAND_COLOR);
